import { computeGamma, isCorrectAnswer, normalizeAnswer, pickPairs, } from './data';
import { timing, variantForLevel, } from './params';
export function makeStimulus(trialIdx, pairsCountValue, level, lang) {
    const variant = variantForLevel(level);
    const clamped = Math.max(4, Math.min(16, Math.round(pairsCountValue)));
    const pairs = pickPairs(lang, variant.difficulty, clamped, trialIdx + 1);
    return {
        trialIdx,
        lang,
        pairs,
        distractorMs: variant.distractorMs,
        targetGamma: variant.targetGamma,
    };
}
export function createJolAdapter(level = 1, lang = 'fr') {
    let state = {
        subPhase: 'idle',
        stim: null,
        feedback: null,
        level,
        variant: variantForLevel(level),
        items: [],
        studyIdx: 0,
        testIdx: 0,
        distractorRemainingMs: 0,
    };
    void lang;
    const listeners = new Set();
    let resolver = null;
    let rejecter = null;
    let trialStart = 0;
    const timers = [];
    let tickInterval = null;
    function clearTimers() {
        while (timers.length > 0) {
            const t = timers.pop();
            if (t !== undefined)
                clearTimeout(t);
        }
        if (tickInterval !== null) {
            clearInterval(tickInterval);
            tickInterval = null;
        }
    }
    function update(patch) {
        state = { ...state, ...patch };
        for (const l of listeners) {
            try {
                l(state);
            }
            catch {
                /* ignored */
            }
        }
    }
    function settle(stim) {
        if (!resolver)
            return;
        const completedItems = state.items.map((it) => ({
            ...it,
            // Si pas de JOL fournie, default à 50 (neutre).
            jol: it.jol ?? 50,
            recalled: it.recalled === null
                ? isCorrectAnswer(it.answerText, it.pair.target)
                : it.recalled,
        }));
        const accuracy = completedItems.length === 0
            ? 0
            : completedItems.filter((it) => it.recalled).length
                / completedItems.length;
        const gamma = computeGamma(completedItems.map((it) => ({
            jol: it.jol ?? 50,
            recalled: it.recalled ?? false,
        })));
        const correct = !Number.isNaN(gamma) && gamma >= stim.targetGamma;
        const r = resolver;
        resolver = null;
        rejecter = null;
        clearTimers();
        r({
            items: completedItems,
            accuracy,
            gamma: Number.isNaN(gamma) ? 0 : gamma,
            reactionTimeMs: Date.now() - trialStart,
            correct,
        });
    }
    return {
        mount() {
            // Idempotent.
        },
        unmount() {
            clearTimers();
            listeners.clear();
            if (rejecter) {
                rejecter(new Error('unmount'));
                rejecter = null;
                resolver = null;
            }
        },
        presentStimulus(stim) {
            clearTimers();
            return new Promise((resolve, reject) => {
                resolver = resolve;
                rejecter = reject;
                const items = stim.pairs.map((p) => ({
                    pair: p,
                    jol: null,
                    recalled: null,
                    answerText: '',
                }));
                update({
                    subPhase: 'study',
                    stim,
                    feedback: null,
                    items,
                    studyIdx: 0,
                    testIdx: 0,
                    distractorRemainingMs: stim.distractorMs,
                });
                trialStart = Date.now();
            });
        },
        async showFeedback(correct) {
            update({
                subPhase: 'feedback',
                feedback: correct ? 'correct' : 'incorrect',
            });
            await new Promise((resolve) => {
                timers.push(setTimeout(() => {
                    update({ subPhase: 'iti' });
                    resolve();
                }, timing.feedbackMs));
            });
        },
        pause() {
            // Sprint 0.5 : no-op.
        },
        resume() {
            // Sprint 0.5 : no-op.
        },
        abort() {
            clearTimers();
            if (rejecter) {
                rejecter(new Error('aborted'));
                rejecter = null;
                resolver = null;
            }
        },
        getState() {
            return state;
        },
        subscribe(listener) {
            listeners.add(listener);
            listener(state);
            return () => {
                listeners.delete(listener);
            };
        },
        submitJol(idx, jol) {
            if (state.subPhase !== 'study')
                return;
            const items = state.items.slice();
            const it = items[idx];
            if (!it)
                return;
            const clamped = Math.max(0, Math.min(100, Math.round(jol)));
            items[idx] = { ...it, jol: clamped };
            update({ items });
        },
        nextStudy() {
            if (state.subPhase !== 'study')
                return;
            const next = state.studyIdx + 1;
            if (next >= state.items.length)
                return;
            update({ studyIdx: next });
        },
        finishStudy() {
            if (state.subPhase !== 'study' || state.stim === null)
                return;
            const stim = state.stim;
            update({
                subPhase: 'distractor',
                distractorRemainingMs: stim.distractorMs,
            });
            const distractorStart = Date.now();
            tickInterval = setInterval(() => {
                const elapsed = Date.now() - distractorStart;
                const remaining = Math.max(0, stim.distractorMs - elapsed);
                if (remaining <= 0) {
                    if (tickInterval !== null) {
                        clearInterval(tickInterval);
                        tickInterval = null;
                    }
                    if (state.subPhase === 'distractor') {
                        update({ subPhase: 'test', testIdx: 0, distractorRemainingMs: 0 });
                    }
                }
                else {
                    update({ distractorRemainingMs: remaining });
                }
            }, 250);
            timers.push(setTimeout(() => {
                if (state.subPhase === 'distractor') {
                    update({ subPhase: 'test', testIdx: 0, distractorRemainingMs: 0 });
                }
            }, stim.distractorMs + 100));
        },
        submitAnswer(idx, answer) {
            if (state.subPhase !== 'test')
                return;
            const items = state.items.slice();
            const it = items[idx];
            if (!it)
                return;
            items[idx] = {
                ...it,
                answerText: normalizeAnswer(answer),
                recalled: isCorrectAnswer(answer, it.pair.target),
            };
            update({ items });
        },
        nextTest() {
            if (state.subPhase !== 'test')
                return;
            const next = state.testIdx + 1;
            if (next >= state.items.length)
                return;
            update({ testIdx: next });
        },
        finishTest() {
            if (state.subPhase !== 'test' || state.stim === null)
                return;
            settle(state.stim);
        },
        setLevel(newLevel) {
            const v = variantForLevel(newLevel);
            update({ level: newLevel, variant: v });
        },
    };
}
//# sourceMappingURL=adapter.js.map